import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { formatPercent } from '@anytag/shared-utils';
import { Anchor, TableCell } from '@anytag/shared-atoms';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { useUserRoles, useInfluencerPermissions } from '@anytag/auth';
import { useGenerateInfluencerProfileLink, useTableInputCursor } from '@anytag/shared-hooks';
import {
  MarketplaceDetail,
  MarketplaceCampaignRevenueType,
  MarketplaceAffiliateJoinedInfluencer
} from '@anytag/graphql';
import { findRevenueByType } from '../../utils';
import { UpdateCostsFormValues } from '../../../hooks';

interface Props {
  marketplace: MarketplaceDetail;
  data: readonly MarketplaceAffiliateJoinedInfluencer[];
}

export const useMarketplaceJoinedInfluencersTable = ({ data, marketplace: { currency } }: Props) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { hideSelectInfluencers } = useInfluencerPermissions();
  const { control } = useFormContext<UpdateCostsFormValues>();
  const { handleInputFocus, handleInputBlur } = useTableInputCursor<UpdateCostsFormValues>();
  const { generateProfileLink } = useGenerateInfluencerProfileLink({ disabled: hideSelectInfluencers });

  const columns: ColumnDef<MarketplaceAffiliateJoinedInfluencer>[] = [
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { id: influencerId, name, avatar } = row.original.influencer;
        const profileLink = generateProfileLink(influencerId);

        return (
          <div className="flex min-w-50 items-center">
            <InfluencerAvatar className="rounded-none" size="48px" src={avatar} id={influencerId} />
            <Anchor className="ml-2" label={name} {...profileLink} />
          </div>
        );
      }
    },
    {
      accessorKey: 'promotionMethods',
      header: t<string>('HeaderColumn.Social Media / Promotion method'),
      cell: ({ row }) => <TableCell.Text value={row.original.promotionMethods.join(', ') || '-'} />
    },
    {
      accessorKey: 'influencerCommissionRate',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Influencer Commission Rate'),
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const influencerCommissionRate = findRevenueByType(
          revenueItems,
          MarketplaceCampaignRevenueType.INFLUENCER_COMMISSION_RATE
        );

        return influencerCommissionRate !== null && isAdminStaff ? (
          <TableCell.ControlledRevenueInput
            rightSection="%"
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            name={`${influencer.id}_influencerCommissionRate`}
            updatedDate={influencerCommissionRate?.updatedDate}
          />
        ) : (
          <TableCell.Text value={formatPercent(influencerCommissionRate?.value, false)} />
        );
      }
    },
    {
      accessorKey: 'revenuePerClick',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Revenue per Click'),
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerClick = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_CLICK);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            name={`${influencer.id}_revenuePerClick`}
            updatedDate={revenuePerClick?.updatedDate}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerAction',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Revenue per Conversion'),
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerAction = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_ACTION);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            name={`${influencer.id}_revenuePerAction`}
            updatedDate={revenuePerAction?.updatedDate}
          />
        );
      }
    },
    {
      size: 50,
      accessorKey: 'joinedDate',
      header: t<string>('HeaderColumn.Joined Date'),
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.joinedDate} />
    }
  ];

  return { columns };
};
